import React, { useState } from 'react';
import SingleSearchForm from './SingleSearchForm';
import { formatDateFR } from '../../utils/formatFR';

/**
 * Gestionnaire de multiples recherches pour un contact acquéreur
 * Permet d'ajouter jusqu'à 5 recherches avec priorités
 */
const MultipleSearchesManager = ({ 
  recherches = [], 
  onUpdate,
  onToggleEquipement,
  rechercheOuverteId = null,   // lot DE — recherche dépliée à l'ouverture (bouton « Ajouter »)
}) => {
  const [expandedRecherche, setExpandedRecherche] = useState(
    rechercheOuverteId || (recherches.length > 0 ? recherches[0].id : null),
  );
  const MAX_RECHERCHES = 5;

  // Ajouter une nouvelle recherche
  const handleAddRecherche = () => {
    if (recherches.length >= MAX_RECHERCHES) {
      alert(`Vous ne pouvez pas ajouter plus de ${MAX_RECHERCHES} recherches.`);
      return;
    }

    const newRecherche = {
      id: `r${Date.now()}`,
      priorite: recherches.length + 1,
      label: `Recherche ${recherches.length + 1}`,
      active: true,
      operation: null,
      profil: null,
      sous_activites: [],
      villes: [],
      secteurs: [],
      budget_min: null,
      budget_max: null,
      droit_entree_min: null,
      droit_entree_max: null,
      surface_min: null,
      surface_max: null,
      equipements_souhaites: {},
      notes: '',
      date_creation: new Date().toISOString()
    };

    onUpdate([...recherches, newRecherche]);
    setExpandedRecherche(newRecherche.id);
  };

  // Supprimer une recherche
  const handleDeleteRecherche = (id) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette recherche ?')) return;
    
    const updatedRecherches = recherches
      .filter(r => r.id !== id)
      .map((r, index) => ({ ...r, priorite: index + 1 })); // Réorganiser les priorités
    
    onUpdate(updatedRecherches);
    
    if (expandedRecherche === id && updatedRecherches.length > 0) {
      setExpandedRecherche(updatedRecherches[0].id);
    }
  };

  // Dupliquer une recherche
  const handleDuplicateRecherche = (recherche) => {
    if (recherches.length >= MAX_RECHERCHES) {
      alert(`Vous ne pouvez pas ajouter plus de ${MAX_RECHERCHES} recherches.`);
      return;
    }

    const duplicated = {
      ...recherche,
      id: `r${Date.now()}`,
      priorite: recherches.length + 1,
      label: `${recherche.label} (copie)`,
      date_creation: new Date().toISOString()
    };

    onUpdate([...recherches, duplicated]);
    setExpandedRecherche(duplicated.id);
  };

  // Mettre à jour une recherche
  const handleUpdateRecherche = (id, field, value) => {
    const updatedRecherches = recherches.map(r => 
      r.id === id ? { ...r, [field]: value } : r
    );
    onUpdate(updatedRecherches);
  };

  // Toggle actif/inactif
  const handleToggleActive = (id) => {
    const updatedRecherches = recherches.map(r => 
      r.id === id ? { ...r, active: !r.active } : r
    );
    onUpdate(updatedRecherches);
  };

  // Changer la priorité
  const handleChangePriorite = (id, direction) => {
    const index = recherches.findIndex(r => r.id === id);
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === recherches.length - 1)
    ) return;

    const newRecherches = [...recherches];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    [newRecherches[index], newRecherches[targetIndex]] = [newRecherches[targetIndex], newRecherches[index]];
    
    // Réorganiser les priorités
    newRecherches.forEach((r, i) => r.priorite = i + 1);
    
    onUpdate(newRecherches);
  };

  // Badge de priorité
  const getPriorityBadge = (priorite) => {
    const colors = {
      1: 'bg-red-500',
      2: 'bg-orange-500',
      3: 'bg-yellow-500',
      4: 'bg-blue-600',
      5: 'bg-gray-500'
    };
    return colors[priorite] || 'bg-gray-500';
  };

  // Obtenir les sous-activités selon le profil
  const getSousActivites = (profil) => {
    const mapping = {
      'chr': [
        'Restaurant / Brasserie',
        'Pizzeria',
        'Snack / Fast-food',
        'Bar / Café / Pub / Lounge',
        'Boulangerie / Pâtisserie',
        'Hôtel / Résidence hôtelière / Chambre d\'hôtes'
      ],
      'retail': [
        'Épicerie / Supérette / Primeur',
        'Salon de coiffure / Institut de beauté / Spa',
        'Pharmacie / Parapharmacie',
        'Tabac / Presse / PMU',
        'Magasin de prêt-à-porter / Chaussures / Bijouterie',
        'Garage / Station-service / Centre auto',
        'Pressing / Blanchisserie',
        'Autres commerces (librairie, fleuriste, animalerie...)'
      ],
      'bureaux': [
        'Bureaux classiques',
        'Cabinet médical / Paramédical',
        'Cabinet d\'avocats / Expertise comptable',
        'Agence immobilière / Assurance',
        'Centre d\'affaires / Coworking'
      ],
      'activite_logistique': [
        'Entrepôt de stockage',
        'Atelier de production',
        'Local artisanal',
        'Showroom'
      ]
    };
    return mapping[profil] || [];
  };

  if (recherches.length === 0) {
    return (
      <section id="recherches" className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 md:p-6 scroll-mt-32 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
              <i className="bi bi-search text-blue-600 text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold text-slate-800">Critères de Recherche</h2>
              <p className="text-xs md:text-sm text-slate-500">Définissez jusqu'à 5 recherches par priorité</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center">
          <i className="bi bi-search text-4xl text-slate-300 mb-3"></i>
          <p className="text-slate-600 mb-4">Aucune recherche définie</p>
          <button
            type="button"
            onClick={handleAddRecherche}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-md transition"
          >
            <i className="bi bi-plus-lg mr-2"></i>
            Ajouter une recherche
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="recherches" className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 md:p-6 scroll-mt-32 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
            <i className="bi bi-search text-blue-600 text-xl"></i>
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-bold text-slate-800">Critères de Recherche</h2>
            <p className="text-xs md:text-sm text-slate-500">{recherches.length}/{MAX_RECHERCHES} recherche(s)</p>
          </div>
        </div>
        
        {recherches.length < MAX_RECHERCHES && (
          <button
            type="button"
            onClick={handleAddRecherche}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm shadow-md transition"
          >
            <i className="bi bi-plus-lg mr-2"></i>
            <span className="hidden sm:inline">Ajouter</span>
          </button>
        )}
      </div>

      <div className="space-y-3">
        {recherches.map((recherche, index) => (
          <div 
            key={recherche.id}
            className={`border-2 rounded-lg overflow-hidden transition ${
              recherche.active ? 'border-blue-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-75'
            }`}
          >
            {/* Header de la recherche */}
            <div 
              data-recherche-id={recherche.id}
              className="flex items-center justify-between p-3 md:p-4 cursor-pointer hover:bg-slate-50"
              onClick={() => setExpandedRecherche(expandedRecherche === recherche.id ? null : recherche.id)}
            >
              <div className="flex items-center gap-3 flex-1">
                <span className={`w-8 h-8 ${getPriorityBadge(recherche.priorite)} text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0`}>
                  {recherche.priorite}
                </span>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-slate-800 text-sm md:text-base truncate">{recherche.label}</h3>
                    {!recherche.active && (
                      <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded-full">En pause</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {recherche.operation ? `${recherche.operation}` : 'Non configurée'} 
                    {recherche.profil && ` • ${recherche.profil}`}
                    {recherche.villes?.length > 0 && ` • ${recherche.villes.join(', ')}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Boutons priorité */}
                <div className="hidden sm:flex gap-1">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleChangePriorite(recherche.id, 'up'); }}
                    disabled={index === 0}
                    className="p-1.5 hover:bg-slate-200 rounded disabled:opacity-30"
                    title="Augmenter la priorité"
                  >
                    <i className="bi bi-arrow-up text-slate-600"></i>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleChangePriorite(recherche.id, 'down'); }}
                    disabled={index === recherches.length - 1}
                    className="p-1.5 hover:bg-slate-200 rounded disabled:opacity-30"
                    title="Diminuer la priorité"
                  >
                    <i className="bi bi-arrow-down text-slate-600"></i>
                  </button>
                </div>

                {/* Menu actions */}
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleToggleActive(recherche.id); }}
                    className="p-1.5 hover:bg-slate-200 rounded"
                    title={recherche.active ? "Mettre en pause" : "Activer"}
                  >
                    <i className={`bi ${recherche.active ? 'bi-pause-fill text-orange-600' : 'bi-play-fill text-green-600'}`}></i>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDuplicateRecherche(recherche); }}
                    className="p-1.5 hover:bg-slate-200 rounded"
                    title="Dupliquer"
                  >
                    <i className="bi bi-files text-blue-600"></i>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDeleteRecherche(recherche.id); }}
                    className="p-1.5 hover:bg-red-50 rounded"
                    title="Supprimer"
                  >
                    <i className="bi bi-trash text-red-600"></i>
                  </button>
                </div>

                <i className={`bi ${expandedRecherche === recherche.id ? 'bi-chevron-up' : 'bi-chevron-down'} text-slate-400 ml-2`}></i>
              </div>
            </div>

            {/* Contenu déplié de la recherche */}
            {expandedRecherche === recherche.id && (
              <div className="p-4 border-t-2 border-slate-100 bg-white">
                <SingleSearchForm 
                  recherche={recherche}
                  onUpdate={(updatedRecherche) => {
                    const updatedRecherches = recherches.map(r => 
                      r.id === updatedRecherche.id ? updatedRecherche : r
                    );
                    onUpdate(updatedRecherches);
                  }}
                  onToggleEquipement={(equipId) => {
                    // Toggle équipement pour cette recherche spécifique
                    const currentEquipements = recherche.equipements_souhaites || {};
                    const newEquipements = { ...currentEquipements };
                    
                    if (newEquipements[equipId]) {
                      delete newEquipements[equipId];
                    } else {
                      newEquipements[equipId] = true;
                    }
                    
                    const updatedRecherche = {
                      ...recherche,
                      equipements_souhaites: newEquipements
                    };
                    
                    const updatedRecherches = recherches.map(r => 
                      r.id === updatedRecherche.id ? updatedRecherche : r
                    );
                    onUpdate(updatedRecherches);
                  }}
                />
                
                <div className="text-xs text-slate-400 mt-4 pt-4 border-t border-slate-200 num">
                  <i className="bi bi-calendar3 mr-1"></i>
                  Créée le {formatDateFR(recherche.date_creation)}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default MultipleSearchesManager;
